import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollText, Archive, LifeBuoy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime, STATUS_LABEL } from "@/lib/format";
import AISupportChat from "@/components/AISupportChat";
import TicketThread from "@/components/TicketThread";

const ARCHIVED_STATUSES = ["resolved", "closed"];

function TicketList({ tickets, onOpen, emptyLabel, testPrefix }) {
  if (tickets.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-stone-500 font-body flex items-center justify-center gap-2">
        <ScrollText className="h-4 w-4" /> {emptyLabel}
      </div>
    );
  }
  return tickets.map((t) => (
    <button
      key={t.id}
      type="button"
      onClick={() => onOpen(t.id)}
      className="p-4 w-full flex items-start gap-4 hover:bg-stone-50 text-left"
      data-testid={`${testPrefix}-${t.id}`}
    >
      <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
        <ScrollText className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-stone-950 truncate flex items-center gap-2">
          {t.subject}
          {(t.unread_by_tenant || 0) > 0 && (
            <Badge className="bg-rose-600 text-white text-[10px] border-none">
              {t.unread_by_tenant}
            </Badge>
          )}
        </div>
        <div className="text-xs text-stone-500 font-body">
          {formatDateTime(t.last_activity_at || t.updated_at || t.created_at)}
        </div>
      </div>
      <Badge variant="outline" className="border-stone-300 shrink-0">
        {STATUS_LABEL[t.status] || t.status}
      </Badge>
    </button>
  ));
}

export default function Support() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "archived" ? "archived" : "active";
  const [tickets, setTickets] = useState([]);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [ticket, setTicket] = useState({ subject: "", message: "", priority: "medium" });

  const load = async () => {
    try {
      const { data } = await api.get("/tickets");
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("tickets failed", err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", ticket);
      toast.success("Chamado aberto. Nossa equipe entrará em contato.");
      setTicket({ subject: "", message: "", priority: "medium" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao abrir chamado");
    }
  };

  const active = tickets.filter((t) => !ARCHIVED_STATUSES.includes(t.status));
  const archived = tickets.filter((t) => ARCHIVED_STATUSES.includes(t.status));

  return (
    <Layout title="Suporte" subtitle="Assistente IA, chamados e histórico da sua operação">
      <div data-testid="support-page" className="max-w-4xl space-y-6">
        <AISupportChat />

        <Card className="p-6 rounded-xl border-stone-200 bg-white">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-4 w-4 text-emerald-700" />
            <h3 className="font-display text-lg font-bold tracking-tight">Abrir chamado</h3>
          </div>
          <p className="text-sm text-stone-500 font-body mb-4">
            Nossa equipe responde em até 24h úteis.
          </p>
          <form onSubmit={submitTicket} className="space-y-3">
            <div>
              <Label>Assunto *</Label>
              <Input
                value={ticket.subject}
                onChange={(e) => setTicket({ ...ticket, subject: e.target.value })}
                required
                className="mt-1 bg-white"
                data-testid="support-subject"
              />
            </div>
            <div>
              <Label>Mensagem *</Label>
              <Textarea
                value={ticket.message}
                onChange={(e) => setTicket({ ...ticket, message: e.target.value })}
                required
                rows={5}
                className="mt-1 bg-white"
                data-testid="support-message"
              />
            </div>
            <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="support-submit">
              Enviar chamado
            </Button>
          </form>
        </Card>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="bg-stone-100 border border-stone-200">
            <TabsTrigger value="active" data-testid="support-tab-active">
              Ativos ({active.length})
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="support-tab-archived">
              <Archive className="h-3.5 w-3.5 mr-1.5" /> Arquivados ({archived.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-4">
            <Card className="rounded-xl border-stone-200 bg-white divide-y divide-stone-200">
              <TicketList
                tickets={active}
                onOpen={setOpenTicketId}
                emptyLabel="Nenhum chamado ativo."
                testPrefix="support-ticket"
              />
            </Card>
          </TabsContent>
          <TabsContent value="archived" className="mt-4">
            <Card className="rounded-xl border-stone-200 bg-white divide-y divide-stone-200">
              <TicketList
                tickets={archived}
                onOpen={setOpenTicketId}
                emptyLabel="Nenhum chamado arquivado ainda."
                testPrefix="support-archived"
              />
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <TicketThread
        ticketId={openTicketId}
        open={!!openTicketId}
        onOpenChange={(o) => !o && setOpenTicketId(null)}
        onUpdated={load}
        viewerRole="tenant"
      />
    </Layout>
  );
}
